import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { FileUpload, AttachmentPreview } from '@/components/FileUpload';
import { Clock, ArrowLeft, Send } from 'lucide-react';

export default function HelpdeskTicket() {
  const { ticketId } = useParams();
  const [, setLocation] = useLocation();
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);

  const { data: ticket } = useQuery<any>({
    queryKey: ['helpdesk-ticket', ticketId],
    queryFn: () => apiRequest('GET', `/api/helpdesk/${ticketId}`),
    refetchInterval: 5000
  });

  const sendMessageMutation = useApiMutation({
    endpoint: () => `/api/helpdesk/${ticketId}/messages`,
    method: "POST",
    invalidate: [['helpdesk-ticket', ticketId], ['helpdesk-tickets-admin'], ['helpdesk-stats']],
    successMessage: "Response sent",
    onSuccess: () => {
      setNewMessage('');
      setAttachments([]);
    },
  });

  const updateStatusMutation = useApiMutation({
    endpoint: () => `/api/helpdesk/${ticketId}/status`,
    method: "PATCH",
    invalidate: [['helpdesk-ticket', ticketId], ['helpdesk-tickets-admin'], ['helpdesk-stats']],
    successMessage: "Status updated",
  });

  const formatWaitTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))}m`;
    return `${Math.floor(ms / (1000 * 60))}m`;
  };

  const getCurrentWaitTime = () => {
    if (!ticket?.waitingForResponse || !ticket?.responseTimerStart) return ticket?.totalWaitTime || 0;
    const current = new Date().getTime() - new Date(ticket.responseTimerStart).getTime();
    return (ticket.totalWaitTime || 0) + current;
  };

  const handleSend = () => {
    sendMessageMutation.mutate({ message: newMessage, attachments, asAdmin: true });
  };

  if (!ticket) return <div className="container mx-auto py-6">Loading...</div>;

  return (
    <div className="container mx-auto py-6 max-w-5xl">
      <Button variant="ghost" onClick={() => setLocation('/admin/helpdesk')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Tickets
      </Button>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{ticket.subject}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {ticket.userName} ({ticket.userEmail})
              </p>
              <div className="flex gap-2 mt-3">
                <Badge variant="outline" className="capitalize">{ticket.category}</Badge>
                {ticket.waitingForResponse && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Waiting {formatWaitTime(getCurrentWaitTime())}
                  </Badge>
                )}
                {ticket.totalWaitTime > 0 && (
                  <Badge variant="secondary">
                    Total wait: {formatWaitTime(ticket.totalWaitTime)}
                  </Badge>
                )}
              </div>
            </div>
            <Select 
              value={ticket.status} 
              onValueChange={(status) => updateStatusMutation.mutate({ status })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent>
          {/* Messages */}
          <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
            {ticket.messages?.map((msg: any) => (
              <div key={msg._id} className={`flex gap-3 ${msg.isAdmin ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{msg.isAdmin ? 'A' : 'U'}</AvatarFallback>
                </Avatar>
                <div className={`flex-1 ${msg.isAdmin ? 'text-right' : ''}`}>
                  <div className={`inline-block p-3 rounded-lg ${
                    msg.isAdmin ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.message}</p>
                    {msg.attachments?.map((att: any) => (
                      <AttachmentPreview key={att.url} url={att.url} fileName={att.fileName} fileType={att.fileType} />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(msg.createdAt).toLocaleString()} {msg.source === 'email' && '(via email)'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Reply */}
          {ticket.status !== 'closed' && (
            <div className="space-y-3 border-t pt-4">
              <Textarea
                placeholder="Type your response..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={4}
              />
              <FileUpload
                endpoint={`/api/helpdesk/${ticketId}/upload`}
                onFileUploaded={(file) => setAttachments((prev) => [...prev, file])}
              />
              <Button 
                onClick={handleSend} 
                disabled={!newMessage.trim() || sendMessageMutation.isPending}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                {sendMessageMutation.isPending ? 'Sending...' : 'Send Response'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
